/**
 * layout — the review session's binding of the shared detail-page surface.
 *
 * THE DOCUMENT IS THE CANVAS, and the panes over it are references to it:
 *
 *   findings    the objections to it, list AND detail   (right, open)
 *   aiUsage     what running the review cost            (right, closed)
 *
 * ONE CONTAINER, not two. Documents used to be a pane of its own on the
 * right, which meant the page carried two docked columns squeezing the
 * document between them — to choose a document, a thing you do occasionally,
 * at the permanent cost of width for the thing you read constantly. Choosing
 * lives in a dropdown off the document's name in the header now, with the
 * same rows and the same figures, and the width it was holding went back to
 * the page.
 *
 * The document has also been the other two things and should be neither: a
 * global side panel at 80% covering the very finding it was evidence for,
 * and a docked pane while the centre still rendered a finding's DETAIL — so
 * the same finding was on screen twice and the document fought the lists for
 * a column.
 *
 * Hence the rule this config exists to hold: the centre draws the document
 * and never a finding, and the findings pane is the ONE place a finding is
 * read and decided.
 *
 * WHY GLOBAL SCOPE, where matters are per-entity: a reviewer has ONE
 * review-shaped workspace. Findings-left / documents-right is how they read
 * every session, so remembering it per session id would mean re-arranging the
 * same layout over and over.
 *
 * The model itself lives in `shared/surface`; this file is review's config
 * plus thin bindings, so call sites pass no config.
 */

import {
  DockSide,
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
  PanelSurface,
  activeSheet as genericActiveSheet,
  bringToFront as genericBringToFront,
  dockScrolls as genericDockScrolls,
  dockWidth as genericDockWidth,
  loadLayout as genericLoadLayout,
  movePanel as genericMovePanel,
  panelZIndex as genericPanelZIndex,
  panelsInDock as genericPanelsInDock,
  readingInset as genericReadingInset,
  resizePanel as genericResizePanel,
  saveLayout as genericSaveLayout,
  setPanelVisible as genericSetPanelVisible,
  togglePanel as genericTogglePanel,
  validateLayout as genericValidateLayout,
} from '../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../shared/surface/defineDetailSurface';
import { isAiUsageEnabled } from '../../../config/featureFlags';

export type { DockSide };

export type ReviewPanelId = 'findings' | 'aiUsage';

/**
 * A FINDING's tabs — its analysis, its notes, its history.
 *
 * They are the findings pane's now, not the canvas's, since that is where a
 * finding is read. They stay in the surface config for the same reason a
 * matter's do: one vocabulary, so the URL parser, the bar and anything that
 * switches tabs all agree on what a tab is. `reviewUrlState` validates
 * `?tab=` against `REVIEW_SURFACE_DEFINITION.resolveTab`.
 */
export type FindingTab = 'analysis' | 'notes' | 'activity';

export const REVIEW_TABS: FindingTab[] = ['analysis', 'notes', 'activity'];
export type ReviewPanelSlot = GenericSlot<ReviewPanelId>;
export type ReviewPanelLayout = GenericLayout<ReviewPanelId>;

/* AI usage is dropped from the surface where the feature is not released —
   no slot, no rail pill, no persisted visibility, so a pane can never open
   onto it. See `config/featureFlags.isAiUsageEnabled`. */
export const REVIEW_PANEL_IDS: ReviewPanelId[] = [
  'findings',
  ...(isAiUsageEnabled() ? (['aiUsage'] as ReviewPanelId[]) : []),
];

const REVIEW_PANES: Record<ReviewPanelId, DetailPaneConfig> = {
  /*
    Findings — the list AND the finding being read.

    RIGHT by default, where a reader's eye goes after the text rather than
    before it, and where the old document list used to sit so the page keeps
    its shape. It moves: drag the pane header to the other dock, or use the
    flip control in it, and the choice persists per user like every other
    part of this layout.

    Wide, because it inherited the finding's whole detail from the canvas —
    issue, legal basis, the before/after of the suggested change, the notes
    thread and the decision buttons — and that was written for a 720px centre
    column.
  */
  findings: {
    label: 'Findings',
    bounds: { min: 320, max: 900 },
    dock: 'right', index: 0, width: 480, visible: true, rail: true,
  },
  /*
    AI USAGE — what running this review cost.

    The SECOND pane over the document, and the file's opening note is still
    true: the document is the canvas and findings are where a finding is read
    and decided. This one is neither. It is a reference readout about the
    session itself — the run that produced the findings, the calls inside it,
    the tokens they burned — and it is CLOSED by default, so a session still
    opens as a document with its objections beside it and nothing else.

    Same 300 floor as the other three surfaces, so the run rows keep their
    face-time-cost baseline wherever the pane is docked.
  */
  aiUsage: {
    label: 'AI usage',
    bounds: { min: 300, max: 900 },
    dock: 'right', index: 1, width: 360, visible: false, rail: true,
  },
};

/**
 * v7 — findings OPEN and docked RIGHT, for everybody.
 *
 * v6 said the same thing and could not deliver it. The server keys layouts on
 * kind:name with no version, so hydration kept handing back the arrangement
 * stored under `detail:review` — findings docked LEFT, from back when that was
 * the default — and overwrote these defaults a beat after first paint. The
 * version is part of the REMOTE name now (see `scopeKey`), so v7 is a record
 * nobody has written and the defaults below are what a review actually opens
 * with. Anyone who preferred it on the left moves it back in one click, and
 * that choice is then theirs and persists.
 */
export const REVIEW_LAYOUT_STORAGE_KEY = 'review:layout:v7';

export const REVIEW_SURFACE_DEFINITION = defineDetailSurface<ReviewPanelId, FindingTab>({
  kind: 'detail',
  // Also the `data-testid` prefix for the shell: `review-surface`, etc.
  name: 'review',
  version: 'v7',
  scope: 'global',
  ids: REVIEW_PANEL_IDS,
  panes: REVIEW_PANES,
  tabs: REVIEW_TABS,
  /* Exactly one, and it is this version's own. See above. */
  legacyKeys: [{ exact: REVIEW_LAYOUT_STORAGE_KEY }],
});

export const REVIEW_PANEL_LABELS: Record<ReviewPanelId, string> =
  REVIEW_SURFACE_DEFINITION.labels;
export const REVIEW_PANEL_BOUNDS: Record<ReviewPanelId, PanelBounds> =
  REVIEW_SURFACE_DEFINITION.bounds;
export const REVIEW_DEFAULT_LAYOUT: ReviewPanelLayout =
  REVIEW_SURFACE_DEFINITION.defaults;

/**
 * The surface keyed the OLD, unscoped way.
 *
 * The pure-function bindings below operate on it, and the migration reads
 * through the same key. Runtime persistence goes through
 * `REVIEW_SURFACE_DEFINITION.surfaceFor(userId)` instead.
 */
export const REVIEW_SURFACE: PanelSurface<ReviewPanelId> =
  REVIEW_SURFACE_DEFINITION.legacySurface();

/* ── bindings ─────────────────────────────────────────────────────── */

export const resizePanel = (l: ReviewPanelLayout, id: ReviewPanelId, w: number) =>
  genericResizePanel(REVIEW_SURFACE, l, id, w);
export const setPanelVisible = (l: ReviewPanelLayout, id: ReviewPanelId, v: boolean) =>
  genericSetPanelVisible(l, id, v);
export const togglePanel = (l: ReviewPanelLayout, id: ReviewPanelId) =>
  genericTogglePanel(l, id);
export const bringToFront = (l: ReviewPanelLayout, id: ReviewPanelId) =>
  genericBringToFront(l, id);
export const movePanel = (
  l: ReviewPanelLayout, id: ReviewPanelId, dock: DockSide, index: number
) => genericMovePanel(REVIEW_SURFACE, l, id, dock, index);
export const panelsInDock = (l: ReviewPanelLayout, dock: DockSide) =>
  genericPanelsInDock(REVIEW_SURFACE, l, dock);
export const dockScrolls = (l: ReviewPanelLayout, dock: DockSide) =>
  genericDockScrolls(REVIEW_SURFACE, l, dock);
export const dockWidth = (l: ReviewPanelLayout, dock: DockSide) =>
  genericDockWidth(REVIEW_SURFACE, l, dock);
export const readingInset = (l: ReviewPanelLayout, dock: DockSide) =>
  genericReadingInset(REVIEW_SURFACE, l, dock);
export const panelZIndex = (l: ReviewPanelLayout, id: ReviewPanelId) =>
  genericPanelZIndex(l, id);
export const validateLayout = (raw: unknown) =>
  genericValidateLayout(REVIEW_SURFACE, raw);
export const loadLayout = (storage?: Pick<Storage, 'getItem'>) =>
  genericLoadLayout(REVIEW_SURFACE, storage);
export const saveLayout = (l: ReviewPanelLayout, storage?: Pick<Storage, 'setItem'>) =>
  genericSaveLayout(REVIEW_SURFACE, l, storage);
export const activeSheet = (l: ReviewPanelLayout) =>
  genericActiveSheet(REVIEW_SURFACE, l);
